import React, { useState } from 'react';
import { MenuProps, Badge, Button, Col, Dropdown, Modal, Row, Tag, Tooltip } from 'antd';
import { differenceInYears } from 'date-fns';
import { EllipsisOutlined, ExclamationCircleOutlined } from 'antd/icons';
import Datetime from 'vl-common/src/components/Datestamp';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import qs from 'vl-common/src/lib/query-string';
import useAuth from '@vl-core/useAuth';
import InternalNotesModal from '@components/Modal/InternalNotesModal';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { useQuery } from '@tanstack/react-query';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { usePatientInfo } from '@components/Clinicians/Session/hooks/usePatientInfo';
import { useTranslation } from 'react-i18next';
import { useEligibilityChecks } from '@hooks/useEligibilityChecks';
import useCase from '@hooks/useCase';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { DocmanConsentTooltipContent } from '@components/Clinicians/Docman/DocmanConsentStatusWithTooltip';
import { CaseReviewApptTypes } from 'vl-common/src/hooks/useAppointmentManagmentActions';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { infoModal } from 'vl-common/src/components/Modals/CustomModals/Info';
import { getMetaAppointmentsQuery } from 'vl-common/src/schemas/getMetaAppointments';
import { getMetaCaseQuery } from 'vl-common/src/schemas/getMetaCase';

export interface TimelineNote {
  added_by: string;
  appt_id: string | null;
  case_id: number;
  category_code: string;
  clin_visible: boolean;
  created_by: string;
  created_date: Date;
  date_added: Date;
  note_id: number;
  note_level: string;
  note_path: string;
  note_text: string;
  parent_id: number;
  ubrn: string | null;
  user_guid: string;
}

const StyledDetail = styled.div`
  display: flex;
  align-items: center;

  .fieldName {
    position: relative;
    margin-right: 6px;
    font-weight: bold;
  }
`;

const CenteredCol = styled(Col)`
  display: flex;
  justify-content: left;
  align-items: center;
`;

const FieldNameIcon = styled.span`
  position: absolute;
  left: -20px;
`;

const AmendmentInfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  padding: 2px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
`;

const UserDetails = () => {
  const { user } = useAuth();
  const router = useRouter();
  const userGuid = user?.user_guid;
  const { appointment } = useSessionAppointment();
  const { data: eligibilityChecks } = useEligibilityChecks();
  const isVirtualServicesOnly = !!eligibilityChecks?.VIRTUAL_ONLY;
  const { data: patientInfo } = usePatientInfo();
  const [apptCase] = useCase(appointment.case_id);

  const activeApptId = appointment?.audit_appt_id || appointment?.appointment_id;
  const { detailsCurrentAppointment } = useAppointmentDetails(activeApptId);

  const { data: caseMeta } = useQuery(getMetaCaseQuery({ case_id: appointment.case_id }));

  const virtualReasonsMeta = caseMeta?.virtual_only_reason_codes;

  const virtualOnlyReason = virtualReasonsMeta?.find(
    (reason) => reason.virtual_only_reason_code === eligibilityChecks.virtual_only_reason
  )?.virtual_only_reason_desc;

  const { data: appointmentsMeta } = useQuery(getMetaAppointmentsQuery());

  const [showInternalNotes, setShowInternalNotes] = useState(false);

  const {
    patient,
    appointment_id,
    add_appt_id,
    audit_appt_id,
    case_id,
    patient_user_guid,
    clinician_user_guid,
    client_case_id
  } = appointment;
  const team = (clinician_user_guid !== userGuid).toString();

  const dispatch = useDispatch();

  const { t } = useTranslation('common');
  const eligibilityWarningModal = () => {
    Modal.warning({
      title: 'Warning',
      centered: true,
      content: (
        <>
          <div dangerouslySetInnerHTML={{ __html: t('eligibilityWarningModalHeader') }} />
          <div dangerouslySetInnerHTML={{ __html: t('eligibilityWarningModalContent') }} />
        </>
      ),
      okButtonProps: { type: 'primary' }
    });
  };

  const isAmendmentAppointment = appointment.appt_type_code === CaseReviewApptTypes.CASEREVADD;

  const additionalAmendmentInfo = detailsCurrentAppointment?.add_info;
  const amendmentReason = appointmentsMeta?.reopen_reasons?.find(
    (r) => r.reopen_code === detailsCurrentAppointment?.add_reason_code
  )?.reopen_desc;
  const patientAge = differenceInYears(new Date(), new Date(patientInfo?.dob));

  const shouldShowAmendmentInfo = isAmendmentAppointment && amendmentReason;

  const showAdditionalAmendmentInfo = () =>
    infoModal({ title: 'Amendment Information', content: additionalAmendmentInfo, centered: true });

  const getDocmanConsentText = () => {
    if (!apptCase?.docman_active) return t('Unavailable');
    return apptCase?.docman_consent ? t('Yes') : t('Withheld consent');
  };

  const { data: previousNotes = [] } = useQuery({
    queryKey: ['previousNotes', appointment_id],
    queryFn: async () => {
      const { patient_notes } = await dispatch(actions.getSharedReferrerNotes(patient_user_guid));
      if (patient_notes) {
        return patient_notes
          .filter((n: TimelineNote) => n.case_id === null || n.case_id === case_id)
          .sort((lhs: TimelineNote, rhs: TimelineNote) => rhs.created_date.getTime() - lhs.created_date.getTime());
      }
      return [];
    }
  });

  const timelineLink = (
    case_id: number,
    appointment_id: number,
    patient_user_guid: string,
    team: string,
    clinician_guid: string
  ) => {
    const reqId = Buffer.from(`${case_id}`).toString('base64');
    const appId = Buffer.from(`${appointment_id}`).toString('base64');
    const patient_guid = Buffer.from(`${patient_user_guid}`).toString('base64');
    const clinician_user_guid = Buffer.from(`${clinician_guid}`).toString('base64');

    const queryParams = { case_id: reqId, appt_id: appId, patient_guid, team, clinician_user_guid };
    const query = qs.stringify(queryParams);
    const url = `/clinician/timeline360?${query}`;

    router
      .push(url, undefined, {
        shallow: true
      })
      .then();
  };

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'viewTimeline') {
      timelineLink(case_id, appointment_id, patient_user_guid, team, clinician_user_guid);
    }
    if (e.key === 'internalNotes') {
      setShowInternalNotes(true);
    }
  };

  const items: MenuProps['items'] = [
    {
      label: 'View patient timeline',
      key: 'viewTimeline'
    },
    {
      label: (
        <Badge count={previousNotes.length} offset={[20, 7]}>
          {t('Internal notes')}
        </Badge>
      ),
      key: 'internalNotes'
    }
  ];

  const menuProps = {
    items,
    onClick: handleMenuClick
  };

  const optionalFields = [
    {
      show: !!virtualOnlyReason,
      component: (
        <CenteredCol key="virtual-only-reason" span={24}>
          <StyledDetail>
            <div className="fieldName">{t('Virtual only reason:')}</div>
            <div data-testid="virtual-only-reason">{virtualOnlyReason}</div>
          </StyledDetail>
        </CenteredCol>
      )
    },
    {
      show: !!shouldShowAmendmentInfo,
      component: (
        <CenteredCol key="amendment-reason" span={24}>
          <StyledDetail>
            <div className="fieldName">{t('Amendment reason:')}</div>
            <div data-testid="patient-info-amendment-reason">
              {amendmentReason}
              {!!additionalAmendmentInfo && (
                <AmendmentInfoIcon data-testid="amendment-info-icon" onClick={showAdditionalAmendmentInfo}>
                  <PlusOutlined style={{ fontSize: 10 }} />
                </AmendmentInfoIcon>
              )}
            </div>
          </StyledDetail>
        </CenteredCol>
      )
    },
    {
      show: !!add_appt_id,
      component: (
        <CenteredCol key="original-appt-id" span={24}>
          <StyledDetail>
            <div className="fieldName">{t('Original Appointment ID:')}</div>
            <div data-testid="patient-info-original-appt-id">{add_appt_id}</div>
          </StyledDetail>
        </CenteredCol>
      )
    }
  ].filter((field) => field.show);

  const columnCount = optionalFields.length ? 3 : 2;
  const colSpan = 24 / columnCount;

  return (
    <div style={{ display: 'flex', padding: '30px 40px 30px 40px', alignItems: 'center' }}>
      <Row gutter={[8, 8]} style={{ flex: 1 }}>
        <Col span={colSpan}>
          <Row gutter={[0, 8]}>
            <CenteredCol span={24}>
              <b data-testid="patient-info-name">{patient}</b>{' '}
              {isVirtualServicesOnly && (
                <Tag
                  icon={<ExclamationCircleOutlined />}
                  color="warning"
                  style={{ marginLeft: 4, cursor: 'pointer' }}
                  onClick={eligibilityWarningModal}
                >
                  {t('Virtual Only')}
                </Tag>
              )}
            </CenteredCol>

            <CenteredCol span={24}>
              <StyledDetail>
                <div className="fieldName">{t('Preferred name:')}</div>
                <div data-testid="patient-info-pref-name">{patientInfo?.pref_name}</div>
              </StyledDetail>
            </CenteredCol>

            <CenteredCol span={24}>
              <StyledDetail>
                <div className="fieldName">{t('DOB:')}</div>
                <div data-testid="patient-info-dob">
                  <Datetime
                    date={patientInfo?.dob}
                    format="Date only (localised) - 03/02/2022"
                    timezone="Europe/London"
                  />{' '}
                  {t('({{age}} years old)', { age: patientAge })}
                </div>
              </StyledDetail>
            </CenteredCol>

            <CenteredCol span={24}>
              <StyledDetail>
                <div className="fieldName">
                  <Tooltip title={<DocmanConsentTooltipContent />} trigger={['hover', 'focus']}>
                    <FieldNameIcon>
                      <InfoCircleOutlined />
                    </FieldNameIcon>
                  </Tooltip>
                  <span>{t('Docman Consent:')}</span>
                </div>
                <div data-testid="patient-info-docman-consent">{getDocmanConsentText()}</div>
              </StyledDetail>
            </CenteredCol>
          </Row>
        </Col>

        <Col span={colSpan}>
          <Row gutter={[0, 8]}>
            <CenteredCol span={24}>
              <StyledDetail>
                <div className="fieldName">{t('Postcode:')}</div>
                <div data-testid="patient-info-postcode">{patientInfo?.postcode}</div>
              </StyledDetail>
            </CenteredCol>

            <CenteredCol span={24}>
              <StyledDetail>
                <div className="fieldName">{t('Membership number:')}</div>
                <div data-testid="patient-info-membership-no">{patientInfo?.membership_no}</div>
              </StyledDetail>
            </CenteredCol>

            <CenteredCol span={24}>
              <StyledDetail>
                <div className="fieldName">{t('Case reference:')}</div>
                <div data-testid="patient-info-case-ref">{client_case_id}</div>
              </StyledDetail>
            </CenteredCol>

            <CenteredCol span={24}>
              <StyledDetail>
                <div className="fieldName">{t('Appointment ID:')}</div>
                <div data-testid="patient-info-appt_id">{audit_appt_id || appointment_id}</div>
              </StyledDetail>
            </CenteredCol>
          </Row>
        </Col>

        {!!optionalFields.length && (
          <Col span={colSpan}>
            <Row gutter={[0, 8]}>{optionalFields.map((field) => field.component)}</Row>
          </Col>
        )}
      </Row>
      <Dropdown data-testid="past-case-details-dropdown" menu={menuProps} trigger={['click']}>
        <Badge count={previousNotes.length} offset={[-10, 30]} size="small">
          <Button
            data-testid="history-and-notes-menu"
            type="text"
            icon={<EllipsisOutlined style={{ fontSize: '28px' }} />}
            style={{ transform: 'rotate(90deg)' }}
          />
        </Badge>
      </Dropdown>
      <InternalNotesModal
        previousNotes={previousNotes}
        showInternalNotes={showInternalNotes}
        setShowInternalNotes={setShowInternalNotes}
      />
    </div>
  );
};

export const PatientInformation = () => {
  const { data: patientInfo } = usePatientInfo();

  return (
    <div>
      {patientInfo && (
        <div data-testid="page-subtitle">
          <UserDetails />
        </div>
      )}
    </div>
  );
};

export default PatientInformation;
